import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, ChevronsUpDown, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable student picker with inline "Create new student" flow.
 *
 * Props:
 *  - customers: current list of students
 *  - value: selected id
 *  - onChange: (id) => void
 *  - onCreated: (student) => void — called after a new student is created; parent
 *    should refresh its `customers` list. The component also selects the new student
 *    via `onChange(newStudent.id)`.
 *  - disabled: boolean
 *  - testId: base data-testid; the trigger uses `<testId>` and the create form uses `<testId>-create-*`
 */
export function StudentPickerWithCreate({
  customers = [],
  value,
  onChange,
  onCreated,
  disabled = false,
  testId = "student-picker",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [seedName, setSeedName] = useState("");

  const selected = useMemo(() => customers.find((c) => c.id === value), [customers, value]);

  // Local filter — cmdk's default filter would also work, but we need `filtered.length === 0`
  // to decide whether to render the "+ Create" fallback, which requires the actual filter result.
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return customers;
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.student_ref || "").toLowerCase().includes(q)
    );
  }, [customers, q]);

  const openCreate = (name) => {
    setSeedName(name || query.trim() || "");
    setCreateOpen(true);
    setOpen(false);
  };

  const handleCreated = (student) => {
    onCreated?.(student);
    onChange?.(student.id);
    setCreateOpen(false);
    setQuery("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid={testId}
          >
            <span className={cn("truncate", !selected && "text-slate-500")}>
              {selected ? selected.name : "Choose student…"}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name, email or Student ID…"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-search`}
            />
            <CommandList>
              {filtered.length === 0 && q && (
                <CommandEmpty className="py-4 text-center text-sm text-slate-500">
                  No student matches “{query.trim()}”.
                </CommandEmpty>
              )}
              {filtered.length === 0 && !q && (
                <CommandEmpty className="py-4 text-center text-sm text-slate-500">
                  Type to search students.
                </CommandEmpty>
              )}
              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.slice(0, 200).map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.id}
                      onSelect={() => { onChange?.(c.id); setOpen(false); setQuery(""); }}
                      data-testid={`${testId}-option-${c.id}`}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{c.name}</div>
                        {(c.email || c.student_ref) && (
                          <div className="text-[11px] text-slate-500 truncate">
                            {[c.student_ref, c.email].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                  {filtered.length > 200 && (
                    <div className="px-3 py-2 text-[11px] text-slate-500 border-t">
                      Showing 200 of {filtered.length}. Refine your search to narrow down.
                    </div>
                  )}
                </CommandGroup>
              )}
              <CommandGroup className="border-t">
                <CommandItem
                  value="__create_new_student__"
                  onSelect={() => openCreate(query)}
                  className="text-emerald-700 aria-selected:bg-emerald-50 aria-selected:text-emerald-800 cursor-pointer"
                  data-testid={`${testId}-create-inline`}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {q
                    ? <>+ Create new student <span className="font-semibold">“{query.trim()}”</span></>
                    : "+ Create new student"}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <InlineCreateStudentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        seedName={seedName}
        onCreated={handleCreated}
        testId={testId}
      />
    </>
  );
}


function InlineCreateStudentDialog({ open, onOpenChange, seedName, onCreated, testId }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Re-seed the name field every time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setForm({ name: seedName || "", email: "", phone: "", company: "", address: "" });
      setError("");
    }
  }, [open, seedName]);

  const canSubmit = form.name.trim() && form.email.trim() && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        address: form.address.trim() || null,
        roles: ["buyer", "learner"],
      };
      const { data } = await api.post("/customers", payload);
      toast.success(`Created ${data.name}`);
      onCreated?.(data);
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      // Backend enforces email uniqueness with a clear detail string; surface it as-is
      // but also branch on the well-known "already exists" wording so we can add the
      // "Select them from the list instead." hint.
      const alreadyExists =
        err.response?.status === 409 ||
        /already exists|duplicate|unique/i.test(detail || "");
      setError(alreadyExists
        ? "A student with this email already exists. Select them from the list instead."
        : detail);
    } finally { setSaving(false); }
  };

  const field = (name, label, type = "text", required = false, placeholder = "") => (
    <div className="space-y-1.5">
      <Label htmlFor={`${testId}-create-${name}`} className={required ? "" : "text-slate-600"}>
        {label}{required ? " *" : <span className="text-slate-400 font-normal ml-1">(optional)</span>}
      </Label>
      <Input
        id={`${testId}-create-${name}`}
        type={type}
        value={form[name]}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        placeholder={placeholder}
        required={required}
        disabled={saving}
        data-testid={`${testId}-create-${name}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid={`${testId}-create-dialog`}>
        <DialogHeader>
          <DialogTitle>Create new student</DialogTitle>
          <DialogDescription>
            This adds a Buyer + Learner profile immediately. You’ll return to the form with the new student already selected — nothing you’ve typed is lost.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {field("name", "Full name", "text", true)}
          {field("email", "Email address", "email", true, "student@example.com")}
          {field("phone", "Phone number", "tel", false, "+60 12 345 6789")}
          {field("company", "Company name", "text", false, "For company-billed invoices")}
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-create-address`} className="text-slate-600">
              Billing address <span className="text-slate-400 font-normal ml-1">(optional)</span>
            </Label>
            <Textarea
              id={`${testId}-create-address`}
              rows={2}
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              disabled={saving}
              data-testid={`${testId}-create-address`}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-rose-700 border border-rose-200 bg-rose-50 p-2 rounded" data-testid={`${testId}-create-error`}>
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)} data-testid={`${testId}-create-cancel`}>Cancel</Button>
            <Button type="submit" disabled={!canSubmit} data-testid={`${testId}-create-submit`}>
              {saving ? "Creating…" : "Create student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
